import { z } from 'zod';
import { employmentApi } from '../../../api';
import { EMPLOYMENT_EVENT_TYPES } from '../../../../constants/employmentEventTypes';
import { EVENT_ACTOR_TYPES } from '../../../../constants/employmentActorTypes';
import { DEFAULT_SOURCE_SYSTEM, toSourceContractId } from '../../../../constants/employmentSourceSystems';

const addContractSchema = z.object({
  employeeId: z.uuid(),
  contractStartDate: z.iso.date('Contract start date must be in ISO format (YYYY-MM-DD)'),
  contractType: z.uuid(),
  contractCompany: z.uuid(),
  engagementModel: z.uuid().optional(),
  contractNumber: z.string().optional(),
  contractEndDate: z.iso.date('Contract end date must be in ISO format (YYYY-MM-DD)').optional(),
  contractEndReason: z.string().min(1).optional(),
  reason: z.string().optional()
}).refine(
  (data) => !data.contractEndDate || data.contractEndDate > data.contractStartDate,
  { message: 'Contract end date must be after the contract start date', path: ['contractEndDate'] }
).refine(
  (data) => !data.contractEndDate || Boolean(data.contractEndReason && data.contractEndReason.trim()),
  { message: 'A fixed-term comment is required when a contract end date is set', path: ['contractEndReason'] }
);

/**
 * ADR-171 C4 (ADR-144 §17) — Add Contract: another contract beside the
 * person's primary, within their Region's limit. One event; the backend
 * checks the limit, that it ends by the primary's end (ADR-153 R1), and
 * creates it without touching anything on the person.
 */
export const addContract = async (payload, actorType = EVENT_ACTOR_TYPES.USER) => {
  const p = addContractSchema.parse(payload);
  const contractId = toSourceContractId(p.contractNumber);
  const fixedTermReason = p.contractEndDate ? p.contractEndReason.trim() : null;

  return await employmentApi.triggerEmploymentEvents({
    metadata: { actorType },
    events: [{
      type: EMPLOYMENT_EVENT_TYPES.CONTRACT_ADDED,
      payload: {
        employeeId: p.employeeId,
        contractStartDate: p.contractStartDate,
        contractTypeId: p.contractType,
        contractCompanyId: p.contractCompany,
        ...(p.engagementModel ? { engagementModelId: p.engagementModel } : {}),
        // ADR-160 §5.4 — always stamped as radar's; the number joins it
        sourceReference: contractId === null ? { source: DEFAULT_SOURCE_SYSTEM } : { source: DEFAULT_SOURCE_SYSTEM, contractId },
        ...(fixedTermReason ? { contractEndDate: p.contractEndDate, contractEndReason: fixedTermReason } : {}),
        ...(p.reason && p.reason.trim() ? { reason: p.reason.trim() } : {})
      }
    }]
  });
};
